import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  ExternalLink,
  GraduationCap,
  Brain,
} from "lucide-react";
import { useSeo } from "../lib/useSeo";

const SITE_URL = "https://railwaycareers.co.uk";

const TRAIN_DRIVER_PREPARATION_PARTNERS = [
  {
    icon: GraduationCap,
    title: "Train Driver Foundation",
    url: "https://traindriverfoundation.com",
    domain: "traindriverfoundation.com",
    description:
      "Structured preparation for aspiring train drivers, including application guidance, interview preparation, professional development courses and comprehensive career preparation packages.",
    features: [
      "Application and interview preparation",
      "Train driver career and development courses",
      "Structured guides, tools and preparation packages",
    ],
    cta: "Visit Train Driver Foundation",
  },
  {
    icon: Brain,
    title: "Train Driver Psychometrics",
    url: "https://traindriverpsychometrics.co.uk",
    domain: "traindriverpsychometrics.co.uk",
    description:
      "Specialist psychometric preparation for train driver applicants, with practice tools covering concentration, vigilance, perception, memory, reaction and other assessment-related abilities.",
    features: [
      "Specialist train driver psychometric practice",
      "Concentration, memory and vigilance exercises",
      "Flexible preparation options for different stages",
    ],
    cta: "Visit Train Driver Psychometrics",
  },
];

const GUIDES = {
  "train-driver": {
    title: "Train Driver",
    plural: "Train Driver Jobs",
    tagline:
      "A complete UK guide to trainee train driver jobs, recruitment, training, psychometric assessments and career progression.",
    salary: "£27,000–£33,000 trainee → £48,000–£68,000 qualified",
    duration: "Training length varies, often 9–12 months",
    keywords: [
      "train driver jobs UK",
      "trainee train driver",
      "how to become a train driver",
      "train driver recruitment",
      "train driver psychometric assessments",
      "train driver salary UK",
    ],
    sections: [
      {
        h: "What the role involves",
        p: "Train drivers operate passenger and freight services safely across the UK rail network. The role involves controlling trains in line with signals, speed limits, route knowledge and operational instructions. Drivers must maintain concentration, follow railway rules precisely and respond safely to incidents, disruption or degraded working.",
      },
      {
        h: "Role summary",
        list: [
          "Typical trainee salary: around £27,000–£33,000 depending on employer and location",
          "Typical qualified salary: around £48,000–£68,000 depending on operator, route and terms",
          "Training often takes around 9–12 months, although this varies",
          "Shift work can include early mornings, late nights, weekends and bank holidays",
          "Employers include Train Operating Companies, Freight Operating Companies and heritage railways",
          "Progression can include instructor, driver manager, standards, training or operational leadership roles",
        ],
      },
      {
        h: "Eligibility",
        list: [
          "Right to work in the UK",
          "Age requirements vary by operator and vacancy",
          "Ability to pass a safety-critical medical",
          "Ability to pass train driver psychometric assessments",
          "Ability to meet eyesight, hearing, colour vision, drug and alcohol requirements",
          "Ability to meet employment, reference and background checks",
          "Willingness to work early, late, night, weekend and bank holiday shifts",
          "Many operators expect applicants to live within a reasonable travelling distance of the depot",
        ],
      },
      {
        h: "Entry routes",
        list: [
          "External trainee train driver recruitment campaigns",
          "Train driver apprenticeship routes offered by some employers",
          "Internal progression from conductor, station, dispatch, depot or other operational railway roles",
          "Transfer from safety-critical, transport, logistics, emergency services, military or operational backgrounds",
        ],
      },
      {
        h: "What is the recruitment process?",
        list: [
          "Online application, screening questions and CV or work history review",
          "Initial screening against essential criteria and suitability for safety-critical work",
          "Online or assessment-centre psychometric assessments",
          "Structured interview covering safety, motivation and behavioural examples",
          "Possible assessment centre exercises depending on the operator",
          "Conditional offer, medical, references and pre-employment checks",
          "Classroom training, rules, traction, route learning and supervised practical driving",
        ],
      },
      {
        h: "What do the psychometric assessments test?",
        p: "Train driver assessments are covered in RIS-3751-TOM and focus on concentration, attention, reaction, memory, rules-based decision making, communication and the ability to remain consistent under pressure. Preparation should focus on understanding the format, improving concentration and building strong safety-focused examples rather than trying to memorise specific questions.",
      },
      {
        h: "Training programme",
        p: "Successful applicants usually complete classroom training, rules training, traction training, route learning and practical driving under supervision. Training length varies by employer, traction type, routes, training capacity and individual progress.",
      },
      {
        h: "Skills employers look for",
        list: [
          "Sustained concentration",
          "Strict rule compliance",
          "Calm decision-making under pressure",
          "Clear communication",
          "Personal responsibility for safety",
          "Reliability and punctuality",
          "Ability to work independently",
          "Professional judgement and situational awareness",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I become a train driver in the UK?",
        a: "You normally apply directly to a Train Operating Company, Freight Operating Company or rail employer when trainee train driver vacancies are advertised. The process usually includes an application, psychometric assessments, interviews, medical checks and a structured training programme.",
      },
      {
        q: "Do I need railway experience to become a train driver?",
        a: "Not always. Many trainee train driver roles are open to applicants from outside the railway, but you must show strong safety awareness, concentration, responsibility, communication skills and motivation for shift-based railway work.",
      },
      {
        q: "How long does train driver training take?",
        a: "Training length varies by employer, route, traction and business need. Many programmes take around 9 to 12 months, although some may take longer.",
      },
      {
        q: "Can a conductor or depot driver become a train driver?",
        a: "Yes. Some train drivers progress internally from conductor, station, depot or other operational railway roles. Internal applicants must still meet the required assessment, medical and training standards.",
      },
    ],
  },

  conductor: {
    title: "Conductor / Train Manager",
    plural: "Conductor Jobs",
    tagline:
      "A UK guide to conductor, train manager and onboard safety-critical railway careers.",
    salary: "£27,000–£33,000 trainee → £32,000–£47,000 qualified",
    duration: "Training length varies, often 8–14 weeks",
    keywords: [
      "conductor jobs UK",
      "train manager jobs",
      "railway conductor",
      "trainee conductor",
      "how to become a conductor",
    ],
    sections: [
      {
        h: "What the role involves",
        p: "Conductors and train managers combine customer service with operational and safety responsibilities. Duties may include train dispatch, door operation, revenue protection, passenger information, accessibility support, incident response and communication with drivers, signallers, control and station teams.",
      },
      {
        h: "Role summary",
        list: [
          "Typical trainee salary: around £27,000–£33,000 depending on employer and location",
          "Typical qualified salary: around £32,000–£47,000 depending on operator and terms",
          "Training length varies by operator and route",
          "Shift patterns can include early mornings, late nights, weekends and bank holidays",
          "Employers include Train Operating Companies",
          "Progression can include mentor, instructor, manager or train driver roles",
        ],
      },
      {
        h: "Eligibility",
        list: [
          "Age requirements vary by operator",
          "Right to work in the UK",
          "Strong customer service and communication skills",
          "Ability to pass medical and drug and alcohol screening",
          "Confidence dealing with difficult or disruptive situations",
          "Ability to work shifts, weekends and bank holidays",
          "Ability to balance customer service with safety-critical responsibilities",
        ],
      },
      {
        h: "Entry routes",
        list: [
          "External trainee conductor recruitment campaigns",
          "Conductor apprenticeships offered by some operators",
          "Internal progression from booking office, platform, dispatch, station or depot roles",
          "Transfer from customer service, transport, hospitality, retail, security or safety-focused roles",
        ],
      },
      {
        h: "Recruitment process",
        list: [
          "Online application and screening questions",
          "Initial review against essential criteria and shift-work suitability",
          "Situational judgement, behavioural or customer-service assessment",
          "Group exercise, role-play or assessment centre where required",
          "Structured competency interview",
          "Medical, references and pre-employment checks",
          "Structured training covering rules, dispatch, revenue protection and customer service",
        ],
      },
      {
        h: "Day-to-day work",
        p: "The role can involve early starts, late finishes, lone working, busy services and challenging customer situations. It suits people who can stay calm, follow rules, communicate clearly and make safe decisions while dealing with passengers.",
      },
      {
        h: "Skills employers look for",
        list: [
          "Clear communication",
          "Calmness under pressure",
          "Confidence dealing with passengers",
          "Ability to apply rules and procedures consistently",
          "Reliability and punctuality",
          "Situational awareness",
          "Customer service",
          "Professional judgement",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a conductor role safety-critical?",
        a: "Many conductor and train manager roles include safety-critical responsibilities, although duties vary by operator, route and traincrew model.",
      },
      {
        q: "Can conductor jobs lead to train driver jobs?",
        a: "They can. Conductor experience may help applicants understand railway operations, shift work, customer situations and safety culture, although it does not guarantee progression into driving.",
      },
      {
        q: "Do conductor roles involve customer service?",
        a: "Yes. Conductors are usually a main point of contact for passengers and must combine customer service with operational safety, revenue protection and incident response.",
      },
    ],
  },

  "depot-driver": {
    title: "Depot Driver / Shunter",
    plural: "Depot Driver and Shunter Jobs",
    tagline:
      "A UK guide to depot driving, shunting, yard movements and operational depot railway careers.",
    salary: "£28,000–£35,000 trainee → £35,000–£47,000 qualified",
    duration: "Training length varies, often 3–6 months",
    keywords: [
      "depot driver jobs",
      "shunter jobs railway",
      "rail depot jobs",
      "train shunter jobs",
      "depot driver salary UK",
    ],
    sections: [
      {
        h: "What the role involves",
        p: "Depot drivers and shunters move trains and rolling stock within depot, yard or siding environments. Work may include positioning units for maintenance, fuelling, cleaning, washing, stabling and preparation for service.",
      },
      {
        h: "Role summary",
        list: [
          "Typical trainee salary: around £28,000–£35,000 depending on employer and location",
          "Typical qualified salary: around £35,000–£47,000 depending on operator and grade",
          "Training often takes around 3–6 months, although this varies",
          "Shift work can include early mornings, late nights, weekends and bank holidays",
          "Employers include Train Operating Companies, Freight Operating Companies and heritage railways",
          "Progression can include minder, instructor, yard controller, depot controller or depot management roles",
        ],
      },
      {
        h: "Depot driving environment",
        p: "Depot driving is safety-critical and requires strict adherence to depot procedures, signalling systems and local operating rules. Although movements usually take place at lower speeds than mainline operations, depot environments can be complex, with multiple vehicles, engineering activity, restricted visibility and staff working nearby.",
      },
      {
        h: "Eligibility",
        list: [
          "Age requirements vary by employer",
          "Right to work in the UK",
          "Ability to pass safety-critical medical checks",
          "Ability to pass drug and alcohol screening",
          "Comfortable working outdoors and around moving trains",
          "Ability to follow depot rules, instructions and local procedures precisely",
          "Previous rail, depot, safety-critical or shift-work experience may help but is not always required",
        ],
      },
      {
        h: "Entry routes",
        list: [
          "External depot driver or shunter recruitment campaigns",
          "Apprenticeship routes offered by some operators",
          "Internal progression from cleaner, platform staff, depot operative, dispatcher or maintenance roles",
          "Transfer from safety-critical, logistics, transport, engineering or operational roles",
        ],
      },
      {
        h: "Recruitment process",
        list: [
          "Online application and initial screening",
          "Assessment of safety awareness, rule compliance and suitability for shift work",
          "Situational judgement, behavioural or aptitude-based tests where used",
          "Structured interview covering procedures, independent working, communication and concentration",
          "Medical and occupational health checks",
          "Drug and alcohol screening",
          "Structured training covering safety procedures, shunting movements, signalling, rules and local depot instructions",
        ],
      },
      {
        h: "Why it can be a strong entry route",
        p: "Depot driving can build useful railway experience, traction familiarity, local operating knowledge and a record of safe shift work. For some applicants, this can support future applications into other operational railway roles, including mainline train driving.",
      },
      {
        h: "Skills employers look for",
        list: [
          "Attention to detail",
          "Concentration and situational awareness",
          "Ability to follow instructions accurately",
          "Clear communication",
          "Calmness in busy depot environments",
          "Reliability and discipline",
          "Spatial awareness",
          "Ability to work independently and as part of a team",
        ],
      },
    ],
    faqs: [
      {
        q: "Is depot driving the same as mainline train driving?",
        a: "No. Depot driving usually involves movements within depot, yard or siding limits, while mainline train driving involves operating services over the wider railway network.",
      },
      {
        q: "Do depot driver roles require previous railway experience?",
        a: "Requirements vary. Some employers prefer previous rail, depot or safety-critical experience, while others may provide training for suitable applicants.",
      },
      {
        q: "Can depot driving lead to mainline train driving?",
        a: "It can help. Depot driving builds rolling stock knowledge, safety awareness and operational railway experience, which may support future train driver applications, although it does not guarantee progression.",
      },
    ],
  },

  fitter: {
    title: "Fitter / Maintenance Engineer",
    plural: "Rail Fitter and Train Maintenance Jobs",
    tagline:
      "A UK guide to rolling stock maintenance, fitter, technician and railway engineering careers.",
    salary: "£19,000–£30,000 starting/apprentice → £35,000–£48,000 qualified",
    duration: "Training varies from apprenticeships to experienced-entry routes",
    keywords: [
      "rail fitter jobs",
      "rolling stock engineer",
      "train maintenance jobs",
      "train maintenance technician",
      "railway engineering jobs",
    ],
    sections: [
      {
        h: "What the role involves",
        p: "Rail fitters and train maintenance technicians inspect, maintain, fault-find and repair trains and rolling stock. Work may include planned maintenance, technical diagnostics, brake systems, doors, traction equipment, electrical systems, component replacement and incident response.",
      },
      {
        h: "Role summary",
        list: [
          "Typical starting salary: around £19,000–£30,000, with the lower end more common for apprenticeships",
          "Typical qualified salary: around £35,000–£48,000 depending on employer, grade and experience",
          "Training duration varies by route, employer and previous experience",
          "Shift work can include early mornings, late nights, weekends and bank holidays",
          "Employers include Train Operating Companies, Freight Operating Companies, ROSCOs, manufacturers and maintenance providers",
          "Progression can include senior technician, team leader, fleet engineer, reliability engineer, depot manager or engineering manager",
        ],
      },
      {
        h: "Engineering environment",
        p: "The role is safety-critical and requires strict adherence to engineering standards, safe systems of work, maintenance documentation and technical specifications. Work may involve heavy equipment, live systems, working at height, working under vehicles and coordinating with depot drivers, control teams and operational staff.",
      },
      {
        h: "Entry routes",
        list: [
          "Rail engineering apprenticeship",
          "Mechanical or electrical qualification",
          "Relevant maintenance, manufacturing, automotive, aerospace or transport engineering experience",
          "Armed forces engineering background",
          "Internal progression from depot-based roles where technical aptitude has been demonstrated",
          "Ability to work shifts in depot or maintenance environments",
        ],
      },
      {
        h: "Minimum requirements",
        list: [
          "Requirements vary by employer and route",
          "Apprenticeships usually require suitable GCSEs, commonly including maths, English and science",
          "Experienced roles normally require suitable technical qualifications or equivalent experience",
          "Mechanical, electrical, maintenance or diagnostic experience may be relevant",
          "Ability to pass medical, occupational health and drug and alcohol checks",
          "Commitment to safety, reliability and procedure-led working",
        ],
      },
      {
        h: "Recruitment process",
        list: [
          "Online application and initial screening",
          "Review of qualifications, engineering experience, right to work and shift availability",
          "Aptitude, mechanical reasoning, electrical reasoning or practical assessments where used",
          "Safety-based scenarios or practical tasks",
          "Competency or technical interview",
          "Medical and occupational health checks",
          "Structured training programme or apprenticeship with supervised depot experience",
        ],
      },
      {
        h: "Skills employers look for",
        list: [
          "Mechanical or electrical aptitude",
          "Fault diagnosis and structured problem-solving",
          "Attention to detail",
          "Ability to interpret manuals, diagrams and schematics",
          "Methodical working",
          "Risk awareness",
          "Teamwork and clear communication",
          "Reliability and commitment to safe systems of work",
        ],
      },
      {
        h: "Career development and progression",
        p: "Qualified technicians can develop specialist knowledge in traction systems, braking systems, doors, diagnostics, wheel lathe, electrical systems or fleet reliability. Longer-term progression may include senior technician, team leader, maintenance supervisor, fleet engineer, reliability engineer, depot engineering manager, training or safety roles.",
      },
    ],
    faqs: [
      {
        q: "Do I need a railway background to become a rail fitter?",
        a: "Not always. Employers may consider applicants with relevant mechanical, electrical, maintenance, manufacturing, automotive, aerospace or armed forces engineering experience.",
      },
      {
        q: "What qualifications help with rail maintenance jobs?",
        a: "Mechanical, electrical, maintenance engineering and apprenticeship qualifications can all be relevant, depending on the employer and role.",
      },
      {
        q: "Are rail fitter jobs safety-critical?",
        a: "Many rail maintenance roles involve safety-critical work because technicians inspect, maintain and repair rolling stock that must be safe and reliable before entering service.",
      },
    ],
  },

  graduate: {
    title: "Rail Graduate Schemes",
    plural: "Rail Graduate Scheme Jobs",
    tagline:
      "A UK guide to graduate schemes across rail operations, infrastructure, engineering, digital, project management and commercial functions.",
    salary: "£28,000 → £36,000 starting",
    duration: "Often 18–36 months",
    keywords: [
      "rail graduate schemes",
      "railway graduate jobs",
      "Network Rail graduate jobs",
      "rail graduate careers",
      "transport graduate schemes",
    ],
    sections: [
      {
        h: "What rail graduate schemes involve",
        p: "Rail graduate schemes are structured early-career programmes designed to develop future professionals, specialists and leaders across the railway. Schemes may include placements across different departments, formal training, mentoring, project work and exposure to operational, engineering, commercial or corporate functions.",
      },
      {
        h: "Common graduate disciplines",
        list: [
          "Civil engineering",
          "Mechanical and electrical engineering",
          "Signalling and telecoms",
          "Project management",
          "Commercial and quantity surveying",
          "Operations and transport planning",
          "Digital, data and technology",
          "Finance, HR, procurement and communications",
        ],
      },
      {
        h: "Who offers rail graduate schemes",
        p: "Graduate opportunities can be found across infrastructure managers, transport bodies, Train Operating Companies, rolling stock businesses, engineering consultancies, contractors and major rail suppliers. Availability changes each recruitment cycle.",
      },
      {
        h: "Typical recruitment process",
        list: [
          "Online application",
          "CV or application screening",
          "Numerical, verbal or situational judgement tests",
          "Video interview or strengths-based interview",
          "Assessment centre",
          "Final interview",
          "Pre-employment checks",
        ],
      },
      {
        h: "Skills employers look for",
        list: [
          "Problem solving",
          "Communication",
          "Commercial awareness",
          "Safety awareness",
          "Adaptability",
          "Data and analytical thinking",
          "Teamwork",
          "Leadership potential",
        ],
      },
    ],
    faqs: [
      {
        q: "When do rail graduate schemes open?",
        a: "Many graduate schemes open months before the start date, often around the autumn recruitment cycle, but exact dates vary by employer.",
      },
      {
        q: "Do rail graduate schemes require an engineering degree?",
        a: "Some do, especially technical schemes, but rail employers also recruit graduates into commercial, project management, finance, technology and operational leadership pathways.",
      },
      {
        q: "Are rail graduate schemes competitive?",
        a: "Yes. Graduate schemes can be highly competitive, particularly with major railway employers and infrastructure organisations. Strong applications usually show motivation, relevant experience, transferable skills and clear understanding of the railway sector.",
      },
    ],
  },

  apprentice: {
    title: "Rail Apprenticeships",
    plural: "Rail Apprenticeship Jobs",
    tagline:
      "A UK guide to railway apprenticeships from entry-level roles to advanced, higher and degree-level pathways.",
    salary: "£18,000 → £28,000 during apprenticeship",
    duration: "Usually 2–6 years depending on level",
    keywords: [
      "rail apprenticeships",
      "railway apprenticeship jobs",
      "train apprenticeship UK",
      "rail engineering apprenticeship",
      "rail operations apprenticeship",
    ],
    sections: [
      {
        h: "Why choose a rail apprenticeship",
        p: "Rail apprenticeships allow you to earn while gaining practical experience, recognised qualifications and industry-specific skills. Opportunities can exist in engineering, operations, customer service, digital, signalling, telecoms, project delivery and business support.",
      },
      {
        h: "Common apprenticeship areas",
        list: [
          "Train driving",
          "Conductor",
          "Rail engineering",
          "Rolling stock maintenance",
          "Signalling and telecoms",
          "Track and infrastructure",
          "Customer service",
          "Operations",
          "Project management",
          "Digital and data",
        ],
      },
      {
        h: "Entry requirements",
        list: [
          "Requirements vary by apprenticeship level and employer",
          "Maths and English are commonly required",
          "Some higher-level apprenticeships require A-levels or equivalent",
          "Applicants usually need to demonstrate motivation, reliability and safety awareness",
          "Some roles may require medical, occupational health or drug and alcohol checks",
        ],
      },
      {
        h: "Typical recruitment process",
        list: [
          "Online application",
          "Eligibility screening",
          "Online aptitude or situational judgement tests",
          "Video interview or telephone interview",
          "Assessment centre or practical task",
          "Final interview",
          "Pre-employment checks",
        ],
      },
      {
        h: "Progression",
        p: "Rail apprenticeships can lead to permanent roles, technician pathways, engineering careers, supervisory roles and further study depending on the programme. Higher and degree apprenticeships may support progression into specialist, technical or management roles.",
      },
    ],
    faqs: [
      {
        q: "Can I apply for rail apprenticeships at 16?",
        a: "Some apprenticeships may be open from 16, but requirements vary by employer, programme and safety restrictions.",
      },
      {
        q: "Are railway apprenticeships paid?",
        a: "Yes. Apprenticeships are paid roles, although pay varies by employer, level, location and year of programme.",
      },
      {
        q: "Do railway apprenticeships lead to permanent jobs?",
        a: "Many apprenticeships are designed to develop people into long-term roles, but permanent employment depends on the employer, performance, business need and available vacancies.",
      },
    ],
  },

  "customer-service": {
    title: "Rail Customer Service",
    plural: "Rail Customer Service Jobs",
    tagline:
      "A UK guide to station, retail, gateline, ticket office, travel centre and contact centre railway careers.",
    salary: "£22,000 → £30,000",
    duration: "Training length varies, often 2–6 weeks",
    keywords: [
      "rail customer service jobs",
      "station assistant jobs",
      "railway ticket office jobs",
      "gateline jobs",
      "train station jobs",
    ],
    sections: [
      {
        h: "What rail customer service roles involve",
        p: "Rail customer service roles support passengers across stations, ticket offices, gatelines, travel centres and contact centres. These roles help passengers with journey information, ticketing, accessibility, disruption, safety and general travel support.",
      },
      {
        h: "Common job titles",
        list: [
          "Customer service assistant",
          "Station assistant",
          "Gateline assistant",
          "Ticket office clerk",
          "Travel centre adviser",
          "Platform assistant",
          "Station dispatcher where required",
          "Contact centre adviser",
        ],
      },
      {
        h: "Why it can be a good first step",
        p: "Customer service roles help applicants understand railway passengers, stations, disruption, accessibility, timetables and operational culture. They can be a strong foundation for future progression into other rail careers.",
      },
      {
        h: "Skills employers look for",
        list: [
          "Clear communication",
          "Calmness under pressure",
          "Problem solving",
          "Reliability and punctuality",
          "Confidence dealing with difficult situations",
          "Commitment to safety and customer care",
          "Patience and professionalism",
        ],
      },
      {
        h: "Recruitment process",
        list: [
          "Online application",
          "Screening questions",
          "Customer-service or situational judgement assessment",
          "Telephone, video or face-to-face interview",
          "Role-play or group exercise where required",
          "Medical, references and pre-employment checks where applicable",
        ],
      },
    ],
    faqs: [
      {
        q: "Do rail customer service jobs need previous railway experience?",
        a: "Usually not. Customer service, retail, hospitality, transport, call centre or public-facing experience may all be relevant.",
      },
      {
        q: "Can station roles lead to other railway careers?",
        a: "Yes. Station and customer service roles can help build rail knowledge, safety awareness and internal experience that may support future applications.",
      },
      {
        q: "Are rail customer service jobs shift-based?",
        a: "Many are. Railway customer service roles can involve early starts, late finishes, weekends, bank holidays and disruption-related working depending on the employer and location.",
      },
    ],
  },

  engineering: {
    title: "Rail Engineering & Signalling",
    plural: "Rail Engineering and Signalling Jobs",
    tagline:
      "A UK guide to track, structures, overhead line, signalling, telecoms and infrastructure engineering careers.",
    salary: "£32,000 → £75,000+",
    duration: "Varies by discipline, qualification route and employer",
    keywords: [
      "rail engineering jobs",
      "signalling jobs UK",
      "rail infrastructure careers",
      "rail telecoms jobs",
      "track engineer jobs",
      "OLE engineer jobs",
    ],
    sections: [
      {
        h: "What rail engineering involves",
        p: "Rail engineering covers the design, installation, inspection, maintenance and renewal of railway infrastructure and systems. This can include track, structures, signalling, telecoms, overhead line equipment, power, drainage, earthworks and other assets required to keep the railway safe and reliable.",
      },
      {
        h: "Common disciplines",
        list: [
          "Track and permanent way",
          "Structures, bridges, tunnels and viaducts",
          "Earthworks and geotechnical engineering",
          "Overhead line equipment",
          "Rail power and electrification",
          "Signalling design, installation, testing and maintenance",
          "Telecoms and operational communications",
          "Asset management and reliability",
        ],
      },
      {
        h: "Routes into rail engineering",
        list: [
          "Rail apprenticeship",
          "Graduate engineering scheme",
          "Direct entry from engineering, construction or utilities",
          "Transfer from armed forces engineering or technical roles",
          "Specialist contractor or infrastructure employer pathway",
          "Further study or professional engineering development route",
        ],
      },
      {
        h: "Skills employers look for",
        list: [
          "Technical competence",
          "Safety awareness",
          "Problem solving",
          "Attention to detail",
          "Ability to work to standards and procedures",
          "Teamwork and communication",
          "Willingness to work shifts, nights or possessions where required",
          "Understanding of risk in live railway environments",
        ],
      },
      {
        h: "Progression",
        p: "Rail engineering careers can progress into technical specialist roles, team leadership, project engineering, asset management, reliability, maintenance delivery, engineering assurance and senior infrastructure management.",
      },
    ],
    faqs: [
      {
        q: "Is rail signalling a good career?",
        a: "Rail signalling can be a strong technical career path because it combines safety, infrastructure, technology and specialist competence. Entry routes vary by employer.",
      },
      {
        q: "Can engineers from other industries move into rail?",
        a: "Yes. Relevant experience from construction, utilities, manufacturing, transport, telecoms or the armed forces may transfer into rail engineering roles.",
      },
      {
        q: "Do rail engineering jobs involve night work?",
        a: "Many infrastructure and signalling roles can involve nights, weekends or possession-based work because some maintenance and renewals are carried out when the railway is closed or quieter.",
      },
    ],
  },
};

export default function RoleGuide() {
  const { slug } = useParams();
  const guide = GUIDES[slug];

  useSeo({
    title: guide
      ? `${guide.title} Jobs UK | Career Guide, Salary & Vacancies`
      : "Railway Role Guides UK | Railway Careers",
    description: guide
      ? `Explore ${guide.title} jobs in the UK railway. Learn about salary, training, eligibility, recruitment stages and live vacancies.`
      : "Explore UK railway role guides, salaries, training routes and live rail vacancies.",
    canonical: guide ? `/careers/${slug}` : "/careers",
    jsonLd: guide
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${guide.title} Jobs UK - Career Guide, Salary and Vacancies`,
          description: guide.tagline,
          author: {
            "@type": "Organization",
            name: "Railway Careers UK",
          },
          publisher: {
            "@type": "Organization",
            name: "Railway Careers UK",
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/logo.png`,
            },
          },
          mainEntityOfPage: `${SITE_URL}/careers/${slug}`,
          about: guide.keywords,
        }
      : undefined,
  });

  if (!guide) return <Navigate to="/careers" replace />;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Railway Career Guides",
        item: `${SITE_URL}/careers`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: guide.title,
        item: `${SITE_URL}/careers/${slug}`,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  const occupationSchema = {
    "@context": "https://schema.org",
    "@type": "Occupation",
    name: guide.title,
    description: guide.tagline,
    occupationLocation: {
      "@type": "Country",
      name: "United Kingdom",
    },
    estimatedSalary: {
      "@type": "MonetaryAmountDistribution",
      name: guide.salary,
      currency: "GBP",
    },
    mainEntityOfPage: `${SITE_URL}/careers/${slug}`,
  };

  const recruitmentSection = guide.sections.find(
    (section) =>
      section.list && /recruitment|process/i.test(section.h)
  );

  const howToSchema = recruitmentSection
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: `How to become a ${guide.title} in the UK`,
        description: `Step-by-step UK recruitment process for ${guide.title} roles, including application, assessments, interview, medical and training.`,
        totalTime: guide.duration,
        estimatedCost: {
          "@type": "MonetaryAmount",
          currency: "GBP",
          value: "0",
        },
        step: recruitmentSection.list.map((item, index) => ({
          "@type": "HowToStep",
          position: index + 1,
          name: item.split(",")[0].split("—")[0].slice(0, 80),
          text: item,
        })),
      }
    : null;

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-navy text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 text-sm text-amber hover:text-bone mb-5"
            data-testid="role-back"
          >
            <ArrowLeft className="w-4 h-4" />
            All railway role guides
          </Link>

          <div className="text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            UK Railway Career Guide
          </div>

          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-tight mt-2">
            {guide.plural} UK
          </h1>

          <p className="text-bone/70 mt-4 text-lg max-w-2xl leading-relaxed">
            {guide.tagline}
          </p>

          <div className="mt-8 flex flex-wrap gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber">
                Indicative salary range
              </div>

              <div className="font-mono font-semibold mt-1">
                {guide.salary}
              </div>
            </div>

            <div className="w-px bg-cream/20" />

            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber">
                Training
              </div>

              <div className="font-mono font-semibold mt-1">
                {guide.duration}
              </div>
            </div>
          </div>

          <p className="text-xs text-bone/50 mt-5 max-w-2xl">
            Salary and training ranges are indicative only. Actual pay,
            conditions, training duration and eligibility requirements vary by
            employer, location, vacancy, grade and experience.
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <section className="bg-card border border-border rounded-lg p-8 mb-14">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            Guide overview
          </div>

          <p className="text-muted-foreground leading-relaxed mt-3">
            This guide explains how to find and apply for {guide.title} roles in
            the UK railway, including typical responsibilities, salary
            expectations, eligibility, training routes, recruitment stages and
            links to current vacancies.
          </p>

          {slug === "train-driver" && (
            <div
              className="mt-5 border-l-4 border-amber bg-amber/5 px-4 py-3 rounded-r"
              data-testid="cornerstone-banner"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-700 font-bold">
                In-depth cornerstone guide
              </div>

              <Link
                to="/train-driver-jobs"
                className="block mt-1 text-ink font-semibold hover:text-amber-700"
              >
                Read the full Train Driver Jobs UK guide → salary bands,
                RIS-3751-TOM assessments, every recruiting operator, live
                vacancies
                <ArrowRight className="inline ml-1 w-4 h-4" />
              </Link>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              asChild
              className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
            >
              <Link to={`/vacancies?role=${slug}`}>
                See live vacancies
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>

            <Button asChild variant="outline">
              <Link to="/depot-finder">Find depots near me</Link>
            </Button>
          </div>
        </section>

        <div className="space-y-12">
          {guide.sections.map((section, index) => (
            <section
              key={section.h}
              className="grid md:grid-cols-[140px_1fr] gap-6"
            >
              <div className="font-mono text-xs text-muted-foreground uppercase tracking-wider pt-1">
                {String(index + 1).padStart(2, "0")} ──
              </div>

              <div>
                <h2 className="font-display font-bold text-ink text-2xl tracking-tight">
                  {section.h}
                </h2>

                {section.p && (
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    {section.p}
                  </p>
                )}

                {section.list && (
                  <ul className="mt-4 space-y-2">
                    {section.list.map((item) => (
                      <li key={item} className="flex gap-3 text-sm">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>

        {slug === "train-driver" && (
          <section
            className="mt-16 bg-navy text-bone rounded-lg p-8 md:p-10"
            data-testid="train-driver-preparation-partners"
          >
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-amber font-semibold">
                <ExternalLink className="w-4 h-4" />
                External preparation partners
              </div>

              <h2 className="font-display font-bold text-3xl mt-3 tracking-tight">
                Specialist train driver preparation
              </h2>

              <p className="text-bone/70 leading-relaxed mt-3">
                Railway Careers helps applicants find vacancies and understand
                the recruitment process. These specialist external platforms
                provide additional training, application support and
                psychometric practice for people preparing for train driver
                recruitment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-8">
              {TRAIN_DRIVER_PREPARATION_PARTNERS.map((partner, index) => {
                const Icon = partner.icon;

                return (
                  <article
                    key={partner.title}
                    className="bg-cream text-ink border border-cream/20 rounded-lg p-6 flex flex-col"
                    data-testid={`train-driver-preparation-partner-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-amber/10 border border-amber/30 flex items-center justify-center text-amber-700 shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>

                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">
                          Specialist preparation
                        </div>

                        <h3 className="font-display font-bold text-xl mt-1 tracking-tight">
                          {partner.title}
                        </h3>

                        <div className="font-mono text-[11px] text-muted-foreground mt-1 break-all">
                          {partner.domain}
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed mt-5">
                      {partner.description}
                    </p>

                    <ul className="mt-5 space-y-2">
                      {partner.features.map((feature) => (
                        <li key={feature} className="flex gap-3 text-sm">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-6">
                      <Button
                        asChild
                        className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
                      >
                        <a
                          href={partner.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${partner.cta} — opens in a new tab`}
                        >
                          {partner.cta}
                          <ExternalLink className="ml-2 w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>

            <p className="text-xs text-bone/50 leading-relaxed mt-6">
              These links open external websites. Any courses, subscriptions,
              purchases or services are provided under the terms and policies
              of the respective provider.
            </p>
          </section>
        )}

        <section className="mt-16 bg-card border border-border rounded-lg p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            <HelpCircle className="w-4 h-4" />
            Frequently asked questions
          </div>

          <h2 className="font-display font-bold text-ink text-3xl mt-3 tracking-tight">
            {guide.title} career questions
          </h2>

          <div className="mt-8 space-y-6">
            {guide.faqs.map((faq) => (
              <div key={faq.q} className="border-t border-border pt-6">
                <h3 className="font-display font-bold text-ink text-xl tracking-tight">
                  {faq.q}
                </h3>

                <p className="text-muted-foreground leading-relaxed mt-2">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 bg-card border border-border rounded-lg p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            Related railway career tools
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-5">
            <Link
              to="/vacancies"
              className="block border border-border rounded-lg p-5 hover:bg-muted transition-colors"
            >
              <div className="font-display font-bold text-ink">
                Rail vacancies
              </div>

              <p className="text-sm text-muted-foreground mt-2">
                Search current UK rail jobs by role, keyword and employer.
              </p>
            </Link>

            <Link
              to="/depot-finder"
              className="block border border-border rounded-lg p-5 hover:bg-muted transition-colors"
            >
              <div className="font-display font-bold text-ink">
                Depot finder
              </div>

              <p className="text-sm text-muted-foreground mt-2">
                Find railway depots and operational locations near your
                postcode.
              </p>
            </Link>

            <Link
              to="/operators"
              className="block border border-border rounded-lg p-5 hover:bg-muted transition-colors"
            >
              <div className="font-display font-bold text-ink">
                Rail employers
              </div>

              <p className="text-sm text-muted-foreground mt-2">
                Browse Train Operating Companies, infrastructure employers and
                contractors.
              </p>
            </Link>
          </div>
        </section>

        <section className="mt-16 bg-navy text-bone rounded-lg p-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-amber font-semibold">
              Next step
            </div>

            <div className="font-display font-bold text-2xl mt-1">
              See live {guide.title} vacancies
            </div>
          </div>

          <Button
            asChild
            size="lg"
            className="bg-amber text-ink hover:bg-lime-600 hover:text-ink"
            data-testid="role-vacancies"
          >
            <Link to={`/vacancies?role=${slug}`}>
              Open vacancies
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(occupationSchema),
        }}
      />

      {howToSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(howToSchema),
          }}
        />
      )}
    </div>
  );
}